import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';
import {
  ArrowLeft, Users, UserCheck, UserX, Clock, Download, Search, ChevronLeft, ChevronRight, Mail,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SubscriberDashboard = () => {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ active: 0, pending: 0, unsubscribed: 0, new_this_week: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const perPage = 25;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSubscribers();
  }, [filter, page]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        status: filter,
        limit: perPage.toString(),
        skip: (page * perPage).toString(),
      });
      if (search) params.set('q', search);

      const res = await fetch(`${BACKEND_URL}/api/subscribers/?${params}`);
      if (res.ok) {
        const data = await res.json();
        setSubscribers(data.subscribers || []);
        setTotal(data.total || 0);
        if (data.stats) setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchSubscribers();
  };

  const handleDeactivate = async (email) => {
    if (!window.confirm(`Deactivate subscriber ${email}?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscribers/${encodeURIComponent(email)}/deactivate`, { method: 'PATCH' });
      if (res.ok) {
        toast.success(`${email} deactivated`);
        fetchSubscribers();
      } else {
        toast.error('Failed to deactivate');
      }
    } catch (err) {
      toast.error('Network error');
    }
  };

  const handleExport = () => {
    window.open(`${BACKEND_URL}/api/subscribers/export/csv?status=${filter}`, '_blank');
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return d; }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="subscriber-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <h1 className="text-xl font-bold text-gray-900">Blog Subscribers</h1>
            </div>
            <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-active">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Active Subscribers</CardDescription>
              <CardTitle className="text-2xl text-emerald-600">{stats.active}</CardTitle>
            </CardHeader>
            <CardContent><UserCheck className="w-6 h-6 text-emerald-500" /></CardContent>
          </Card>
          <Card data-testid="stat-new-week">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">New This Week</CardDescription>
              <CardTitle className="text-2xl text-blue-600">+{stats.new_this_week}</CardTitle>
            </CardHeader>
            <CardContent><Users className="w-6 h-6 text-blue-500" /></CardContent>
          </Card>
          <Card data-testid="stat-pending">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Pending Verification</CardDescription>
              <CardTitle className="text-2xl text-amber-600">{stats.pending}</CardTitle>
            </CardHeader>
            <CardContent><Clock className="w-6 h-6 text-amber-500" /></CardContent>
          </Card>
          <Card data-testid="stat-unsubscribed">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs">Unsubscribed</CardDescription>
              <CardTitle className="text-2xl text-red-600">{stats.unsubscribed}</CardTitle>
            </CardHeader>
            <CardContent><UserX className="w-6 h-6 text-red-500" /></CardContent>
          </Card>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex gap-2">
            {[
              { key: 'active', label: 'Active' },
              { key: 'pending', label: 'Pending' },
              { key: 'unsubscribed', label: 'Unsubscribed' },
              { key: 'all', label: 'All' },
            ].map((f) => (
              <Button
                key={f.key}
                variant={filter === f.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilter(f.key); setPage(0); }}
                className={filter === f.key ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <form onSubmit={handleSearch} className="flex gap-2 flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="search-btn">Search</Button>
          </form>
        </div>

        {/* Subscriber Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="subscriber-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Email</th>
                  <th className="text-left p-3 font-medium text-gray-600">Name</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">Subscribed</th>
                  <th className="text-right p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="border-b">
                      <td colSpan={5} className="p-3"><div className="h-5 bg-gray-200 rounded animate-pulse" /></td>
                    </tr>
                  ))
                ) : subscribers.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-gray-500">No subscribers found.</td></tr>
                ) : (
                  subscribers.map((sub, i) => (
                    <tr key={sub.email || i} className="border-b hover:bg-gray-50" data-testid={`subscriber-row-${i}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-gray-900 truncate max-w-[220px]">{sub.email}</span>
                        </div>
                      </td>
                      <td className="p-3 text-gray-700">{sub.name || '-'}</td>
                      <td className="p-3">
                        {sub.active && sub.verified ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        ) : !sub.verified ? (
                          <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Unsubscribed</Badge>
                        )}
                      </td>
                      <td className="p-3 text-gray-500 text-xs">{formatDate(sub.subscribed_at)}</td>
                      <td className="p-3 text-right">
                        {sub.active && sub.verified && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                            onClick={() => handleDeactivate(sub.email)}
                            data-testid={`deactivate-${i}`}
                          >
                            Deactivate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-gray-500">
                Showing {page * perPage + 1}-{Math.min((page + 1) * perPage, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
      <Toaster />
    </div>
  );
};

export default SubscriberDashboard;
